import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  TooltipProps,
  tooltipClasses,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchBox from './SearchBox';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { dsnPageTestId, dsnPageRowTestId } from './test-ids';
import {
  getDailySessionOtherPatientList,
  getDailySessionPatientList,
  resetArchiveDailySessionNote,
  resetDeleteDailySessionNote,
} from 'src/redux/actions/dailySessionNotes';
import moment from 'moment';
import type { SVGProps } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import axios from 'axios';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router';
import {
  resetNewDocumentSignAction,
  resetSignOnBehalfAction,
  resetUndoDocumentSignAction,
} from 'src/redux/actions/documentationAction';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: ' rgba(0, 0, 0, 0.24) 0px 3px 8px',
    fontSize: 11,
  },
}));

const EvaluationIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M2.8125 2.25V10.6875C2.8125 11.1351 2.99029 11.5643 3.30676 11.8807C3.62323 12.1972 4.05245 12.375 4.5 12.375H6.1875M2.8125 2.25H1.6875M2.8125 2.25H15.1875M6.1875 12.375H11.8125M6.1875 12.375L5.4375 14.625M15.1875 2.25H16.3125M15.1875 2.25V10.6875C15.1875 11.1351 15.0097 11.5643 14.6932 11.8807C14.3768 12.1972 13.9476 12.375 13.5 12.375H11.8125M11.8125 12.375L12.5625 14.625M5.4375 14.625H12.5625M5.4375 14.625L5.0625 15.75M12.5625 14.625L12.9375 15.75M5.625 9L7.875 6.75L9.486 8.361C10.2429 7.27447 11.2291 6.36737 12.375 5.70375"
      stroke="#6C737F"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const TherapyIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M11.7495 2.916C11.6495 2.56223 11.4367 2.25079 11.1435 2.02904C10.8503 1.80729 10.4926 1.68736 10.125 1.6875H7.875C7.1025 1.6875 6.45 2.20725 6.2505 2.916M11.7495 2.916C11.7908 3.0615 11.8125 3.216 11.8125 3.375C11.8125 3.52418 11.7532 3.66726 11.6477 3.77275C11.5423 3.87824 11.3992 3.9375 11.25 3.9375H6.75C6.60082 3.9375 6.45774 3.87824 6.35225 3.77275C6.24676 3.66726 6.1875 3.52418 6.1875 3.375C6.1875 3.216 6.21 3.0615 6.2505 2.916M11.7495 2.916C12.234 2.95275 12.7155 2.9985 13.1948 3.054C14.0198 3.15 14.625 3.86175 14.625 4.69275V14.625C14.625 15.0726 14.4472 15.5018 14.1307 15.8182C13.8143 16.1347 13.3851 16.3125 12.9375 16.3125H5.0625C4.61495 16.3125 4.18573 16.1347 3.86926 15.8182C3.55279 15.5018 3.375 15.0726 3.375 14.625V4.69275C3.375 3.86175 3.9795 3.15 4.80525 3.054C5.28603 2.99833 5.76787 2.95232 6.2505 2.916"
      stroke="#6C737F"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M6.375 10.125H11.625M9 7.5V12.75"
      stroke="#6C737F"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const GroupTherapyIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M13.0329 11.5533C13.3838 11.3137 13.5566 11.2658 13.9812 11.2503C14.4057 11.2349 14.826 11.3399 15.1934 11.5533C15.5607 11.7667 15.8601 12.0798 16.057 12.4563C16.2538 12.8328 16.3401 13.2573 16.3057 13.6807C15.4059 13.9953 14.4501 14.1176 13.5 14.04C13.4971 13.19 13.3273 11.9677 12.8692 11.2516C12.4627 10.6139 11.8268 9.91653 11.1636 9.55313C10.5004 9.18973 9.75624 8.99949 8.99999 9C8.24387 8.99961 7.49986 9.18992 6.83679 9.55331C6.17371 9.9167 5.53723 10.614 5.13073 11.2516M13.4992 14.0392L13.5 14.0625C13.5 14.2312 13.491 14.3978 13.4722 14.562C12.1113 15.3428 10.5691 15.7525 8.99999 15.75C7.37249 15.75 5.84474 15.318 4.52774 14.562C4.50846 14.3884 4.49919 14.2139 4.49999 14.0392M4.49999 14.0392C3.55021 14.1197 2.59492 13.9978 1.69574 13.6815C1.6615 13.2582 1.74778 12.8338 1.94461 12.4574C2.14144 12.0811 2.44077 11.7681 2.80799 11.5547C3.17521 11.3413 3.59534 11.2362 4.01978 11.2516C4.44422 11.267 4.87482 11.5533 5.13073 11.2516M4.49999 14.0392C4.50268 13.1893 4.67295 11.9677 5.13073 11.2516M11.25 5.0625C11.25 5.65924 11.0129 6.23153 10.591 6.65349C10.169 7.07545 9.59673 7.3125 8.99999 7.3125C8.40325 7.3125 7.83096 7.07545 7.409 6.65349C6.98704 6.23153 6.74999 5.65924 6.74999 5.0625C6.74999 4.46576 6.98704 3.89347 7.409 3.47151C7.83096 3.04955 8.40325 2.8125 8.99999 2.8125C9.59673 2.8125 10.169 3.04955 10.591 3.47151C11.0129 3.89347 11.25 4.46576 11.25 5.0625ZM15.75 7.3125C15.75 7.53411 15.7063 7.75354 15.6215 7.95828C15.5367 8.16302 15.4124 8.34904 15.2557 8.50574C15.099 8.66244 14.913 8.78674 14.7083 8.87155C14.5035 8.95635 14.2841 9 14.0625 9C13.8409 9 13.6214 8.95635 13.4167 8.87155C13.212 8.78674 13.0259 8.66244 12.8692 8.50574C12.7125 8.34904 12.5882 8.16302 12.5034 7.95828C12.4186 7.75354 12.375 7.53411 12.375 7.3125C12.375 6.86495 12.5528 6.43573 12.8692 6.11926C13.1857 5.80279 13.6149 5.625 14.0625 5.625C14.51 5.625 14.9393 5.80279 15.2557 6.11926C15.5722 6.43573 15.75 6.86495 15.75 7.3125ZM5.62499 7.3125C5.62499 7.53411 5.58134 7.75354 5.49654 7.95828C5.41173 8.16302 5.28743 8.34904 5.13073 8.50574C4.97403 8.66244 4.788 8.78674 4.58327 8.87155C4.37853 8.95635 4.15909 9 3.93749 9C3.71588 9 3.49645 8.95635 3.29171 8.87155C3.08697 8.78674 2.90094 8.66244 2.74425 8.50574C2.58755 8.34904 2.46325 8.16302 2.37844 7.95828C2.29364 7.75354 2.24999 7.53411 2.24999 7.3125C2.24999 6.86495 2.42778 6.43573 2.74425 6.11926C3.06071 5.80279 3.48994 5.625 3.93749 5.625C4.38504 5.625 4.81426 5.80279 5.13073 6.11926C5.4472 6.43573 5.62499 6.86495 5.62499 7.3125Z"
      stroke="#6C737F"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M7 12H11M9 10.5V13.5"
      stroke="#6C737F"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);
const InsuranceCallIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M1.6875 5.0625C1.6875 11.2755 6.7245 16.3125 12.9375 16.3125H14.625C15.0726 16.3125 15.5018 16.1347 15.8182 15.8182C16.1347 15.5018 16.3125 15.0726 16.3125 14.625V13.596C16.3125 13.209 16.0493 12.8715 15.6735 12.7778L12.3563 11.9482C12.0263 11.8657 11.6798 11.9895 11.4765 12.261L10.749 13.2308C10.5375 13.5128 10.1722 13.6372 9.8415 13.5157C8.61366 13.0643 7.49861 12.3514 6.57359 11.4264C5.64856 10.5014 4.93566 9.38634 4.48425 8.1585C4.36275 7.82775 4.48725 7.4625 4.76925 7.251L5.739 6.5235C6.01125 6.32025 6.13425 5.973 6.05175 5.64375L5.22225 2.3265C5.1766 2.14402 5.07129 1.98203 4.92304 1.86625C4.77479 1.75048 4.5921 1.68756 4.404 1.6875H3.375C2.92745 1.6875 2.49822 1.86529 2.18176 2.18176C1.86529 2.49822 1.6875 2.92745 1.6875 3.375V5.0625Z"
      stroke="#6C737F"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const ScreeningsIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M8.25 5.45833H12M8.25 8.625H12M8.25 11.7917H12M7.5 1.5H4.125C3.504 1.5 3 2.032 3 2.6875V14.5625C3 15.218 3.504 15.75 4.125 15.75H13.875C14.496 15.75 15 15.218 15 14.5625V2.6875C15 2.032 14.496 1.5 13.875 1.5H7.5ZM6 5.45833H6.008V5.46678H6V5.45833ZM6 8.625H6.008V8.63345H6V8.625ZM6 11.7917H6.008V11.8001H6V11.7917Z"
      stroke="#6C737F"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const ConcurrentIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="16"
    viewBox="0 0 15 16"
    fill="none"
  >
    <path
      d="M10.4811 10.712C10.8709 10.4458 11.0629 10.3926 11.5347 10.3754C12.0064 10.3582 12.4734 10.4749 12.8816 10.712C13.2897 10.9491 13.6224 11.297 13.8412 11.7153C14.0599 12.1336 14.1557 12.6053 14.1176 13.0758C13.1177 13.4253 12.0557 13.5613 11.0001 13.475C10.9968 12.5305 10.8082 11.1724 10.2992 10.3768C9.84748 9.66826 9.14098 8.89336 8.40408 8.48959C7.66717 8.08581 6.84033 7.87443 6.00005 7.875C5.15992 7.87457 4.33324 8.08602 3.59649 8.48979C2.85975 8.89356 2.15255 9.66838 1.70088 10.3768C1.19223 11.1725 1.00305 12.5298 1.00005 13.4742C0.999166 13.6682 1.00946 13.8621 1.03089 14.055C2.49422 14.895 4.19172 15.375 6.00005 15.375C7.74346 15.3778 9.45702 14.9226 10.9692 14.055C10.9901 13.8725 11.0001 13.6875 11.0001 13.5L10.9992 13.4742M8.50005 3.5C8.50005 4.16304 8.23666 4.79893 7.76782 5.26777C7.29898 5.73661 6.66309 6 6.00005 6C5.33701 6 4.70113 5.73661 4.23229 5.26777C3.76344 4.79893 3.50005 4.16304 3.50005 3.5C3.50005 2.83696 3.76344 2.20107 4.23229 1.73223C4.70113 1.26339 5.33701 1 6.00005 1C6.66309 1 7.29898 1.26339 7.76782 1.73223C8.23666 2.20107 8.50005 2.83696 8.50005 3.5ZM13.5001 6C13.5001 6.24623 13.4516 6.49005 13.3573 6.71753C13.2631 6.94502 13.125 7.15171 12.9509 7.32582C12.7768 7.49993 12.5701 7.63805 12.3426 7.73227C12.1151 7.8265 11.8713 7.875 11.6251 7.875C11.3788 7.875 11.135 7.8265 10.9075 7.73227C10.68 7.63805 10.4733 7.49993 10.2992 7.32582C10.1251 7.15171 9.98701 6.94502 9.89278 6.71753C9.79855 6.49005 9.75005 6.24623 9.75005 6C9.75005 5.50272 9.9476 5.02581 10.2992 4.67417C10.6509 4.32254 11.1278 4.125 11.6251 4.125C12.1223 4.125 12.5992 4.32254 12.9509 4.67417C13.3025 5.02581 13.5001 5.50272 13.5001 6Z"
      stroke="#111927"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 11.75H7.5M6 10.5V13"
      stroke="#111927"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const iconList = [
  {
    name: 'EVALUATION',
    icon: <EvaluationIcon style={{ backgroundColor: '#6C737F' }} />,
  },
  {
    name: 'THERAPY',
    icon: <TherapyIcon />,
  },
  {
    name: 'GROUP_THERAPY',
    icon: <GroupTherapyIcon />,
  },
  {
    name: 'INSURANCE_CALL',
    icon: <InsuranceCallIcon />,
  },
  {
    name: 'SCREENINGS',
    icon: <ScreeningsIcon />,
  },
  {
    name: 'CONCURRENT',
    icon: <ConcurrentIcon />,
  },
];
const sortMethod1 = (type1: any, type2: any) => {
  const groupType1 = type1?.taskNames?.find((name: any) => name?.includes('Group'))?.slice(-1);
  const groupType2 = type2?.taskNames?.find((name: any) => name?.includes('Group'))?.slice(-1);
  const status1 = type1?.status;
  const status2 = type2?.status;

  if (groupType1 === groupType2) {
    if (status1 === 'IN_PROGRESS' && status2 === 'NOT_STARTED') return -1;
    else if (status1 === 'NOT_STARTED' && status2 === 'IN_PROGRESS') return 1;
    else if (status1 === 'NOT_STARTED' && status2 !== 'NOT_STARTED' && status2 !== 'IN_PROGRESS')
      return -1;
    else if (type1?.lastName?.toLowerCase() < type2?.lastName?.toLowerCase()) return -1;
    else if (type1?.lastName?.toLowerCase() === type2?.lastName?.toLowerCase()) return 0;
    else return 1;
  } else if (groupType1 < groupType2) return -1;
  else return 1;
};
const sortMethod2 = (type1: any, type2: any) => {
  const groupType1 = type1?.taskNames?.find((name: any) => name?.includes('Concurrent'))?.slice(-1);
  const groupType2 = type2?.taskNames?.find((name: any) => name?.includes('Concurrent'))?.slice(-1);
  const status1 = type1?.status;
  const status2 = type2?.status;

  if (groupType1 === groupType2) {
    if (status1 === 'IN_PROGRESS' && status2 === 'NOT_STARTED') return -1;
    else if (status1 === 'NOT_STARTED' && status2 === 'IN_PROGRESS') return 1;
    else if (status1 === 'NOT_STARTED' && status2 !== 'NOT_STARTED' && status2 !== 'IN_PROGRESS')
      return -1;
    else if (type1?.lastName?.toLowerCase() < type2?.lastName?.toLowerCase()) return -1;
    else if (type1?.lastName?.toLowerCase() === type2?.lastName?.toLowerCase()) return 0;
    else return 1;
  } else if (groupType1 < groupType2) return -1;
  else return 1;
};
const sortMethod3 = (type1: any, type2: any) => {
  if (type1?.lastName?.toLowerCase() < type2?.lastName?.toLowerCase()) return -1;
  else if (type1?.lastName?.toLowerCase() === type2?.lastName?.toLowerCase()) return 0;
  else return 1;
};

const sortingByTask = (List: any) => {
  const groupArray = List?.filter(
    (patient: any) => patient?.therapyTypes?.includes('GROUP_THERAPY')
  );
  const concurrentArray = List?.filter(
    (patient: any) =>
      patient?.therapyTypes?.includes('CONCURRENT') &&
      !patient?.therapyTypes?.includes('GROUP_THERAPY')
  );
  const individualArray = List?.filter(
    (patient: any) =>
      !patient?.therapyTypes?.includes('CONCURRENT') &&
      !patient?.therapyTypes?.includes('GROUP_THERAPY')
  );
  groupArray.sort(sortMethod1);
  concurrentArray.sort(sortMethod2);
  individualArray.sort(sortMethod3);

  return [...groupArray, ...concurrentArray, ...individualArray];
};

const PatientListing = ({
  setIsOpen,
  isOpen,
  selectedPatient,
  setSelectedPatient,
  selectedDate,
  //  BRK-2075: the on-behalf target ({uuid, name, discipline} | null). When set, both patient
  //  lists key to the TARGET; absent (every legacy mount) the component is byte-identical.
  onBehalfTarget = null,
}: any) => {
  const isMiniTablet = useMediaQuery((theme: any) => theme.breakpoints.down('800'));
  const observer = useRef<any>();

  const [otherPatientList, setOtherPatientList] = useState<any>([]);
  const isAirTablet = useMediaQuery((theme: any) => theme.breakpoints.down('850'));
  const [searchData, setSearchData] = useState('');
  const dispatch = useDispatch();
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const providerData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );
  const patientRecords = useSelector((state: any) => state?.getDailySessionPatientList?.response);
  const effectiveProviderId = onBehalfTarget?.uuid ?? providerId;
  const effectiveDiscipline = onBehalfTarget?.discipline ?? providerData?.discipline;

  const patientResponse = useSelector((state: any) => state?.getDailySessionPatientList);
  const [patientList, setPatientList] = useState<any>([]);
  const location = useLocation();
  const stateDashboard = location?.state;
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);
  const otherPatientData = useSelector(
    (state: any) => state?.getDailySessionOtherPatientList?.response
  );
  const [pageNo, setPageNo] = useState(0);
  // console.log(otherPatientData);
  const soapNotesResponse = useSelector(
    (state: any) => state?.updateSoapNoteReducer?.response?.data
  );
  const patientSourceAbort = axios.CancelToken.source();
  const otherPatientSourceAbort = axios.CancelToken.source();

  const isOtherPatientDataLoading = useSelector(
    (state: any) => state?.getDailySessionOtherPatientList?.loading
  );
  useEffect(() => {
    if (patientRecords) {
      const groupName = new Set();
      const patientNotPresentInGroup = new Set();
      for (let i = 0; i < patientRecords?.length; i++) {
        if (patientRecords[i]?.status === 'IN_PROGRESS') {
          for (let j = 0; j < patientRecords[i]?.taskNames?.length; j++)
            groupName.add(patientRecords[i]?.taskNames[j]);
        }
      }
      const InProgressPatient = patientRecords?.filter((patient: any) => {
        const isProgressPart = patient?.taskNames?.filter((names: any) => {
          if (groupName?.has(names)) return true;
          else return false;
        });
        if (patient?.status === 'IN_PROGRESS' || isProgressPart?.length > 0) {
          patientNotPresentInGroup.add(patient?.uuid);
          return true;
        } else return false;
      });
      const notStartedGroupName = new Set();
      for (let i = 0; i < patientRecords?.length; i++) {
        if (patientRecords[i]?.status === 'NOT_STARTED') {
          for (let j = 0; j < patientRecords[i]?.taskNames?.length; j++) {
            if (!groupName?.has(patientRecords[i]?.taskNames[j]))
              notStartedGroupName.add(patientRecords[i]?.taskNames[j]);
          }
        }
      }
      const NotStartedPatient = patientRecords?.filter((patient: any) => {
        const notStartedPart = patient?.taskNames?.filter((names: any) => {
          if (notStartedGroupName?.has(names) && !groupName?.has(names)) return true;
          else return false;
        });
        if (
          notStartedPart?.length !== 0 ||
          (patient?.status === 'NOT_STARTED' && patient?.taskNames?.length === 0)
        ) {
          patientNotPresentInGroup.add(patient?.uuid);
          return true;
        } else return false;
      });
      const pendingPatient = patientRecords?.filter((patient: any) => {
        return !patientNotPresentInGroup.has(patient?.uuid);
      });
      const sortedInProgressPatient = sortingByTask(InProgressPatient || []);
      const sortedNotStartedPatient = sortingByTask(NotStartedPatient || []);
      const sortedPendingPatient = sortingByTask(pendingPatient || []);
      const updatedList = [
        ...sortedInProgressPatient,
        ...sortedNotStartedPatient,
        ...sortedPendingPatient,
      ];
      if (patientResponse?.response === null) return;
      if (patientRecords?.length === 0 && otherPatientData?.content?.length === 0)
        setSelectedPatient(null);
      else if (selectedPatient) {
        const filterPatientData = updatedList?.filter(
          (patient) => patient?.uuid === selectedPatient?.uuid
        );
        const filterOtherPatientData = otherPatientData?.content?.filter(
          (patient: any) => patient?.uuid === selectedPatient?.uuid
        );
        if (filterPatientData?.length === 0 && filterOtherPatientData?.length === 0) {
          if (updatedList?.length > 0) setSelectedPatient(updatedList[0]);
          else if (otherPatientData?.content?.length > 0)
            setSelectedPatient(otherPatientData?.content[0]);
        } else if (filterPatientData?.length > 0) setSelectedPatient(filterPatientData[0]);
        else if (filterOtherPatientData?.length > 0) setSelectedPatient(filterOtherPatientData[0]);
      } else if (patientRecords?.length > 0) {
        if (updatedList?.length > 0 && !selectedPatient) {
          setSelectedPatient(updatedList[0]);
        }
      } else if (otherPatientData?.content?.length > 0 && !selectedPatient) {
        setSelectedPatient(otherPatientData?.content[0]);
      }

      setPatientList(updatedList);
    }
  }, [patientRecords, otherPatientData?.content]);

  const lastPostElementRef = useCallback((node: any) => {
    // if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPageNo((prevPage) => prevPage + 1); // trigger loading of new posts by chaging page no
      }
    });

    if (node) observer.current.observe(node);
  }, []);

  useEffect(() => {
    callGetPatientList();
    return () => {
      patientSourceAbort.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData, selectedDate, onBehalfTarget?.uuid]);
  const callOtherSessionOther = () => {
    dispatch(
      getDailySessionOtherPatientList(
        {
          localDate: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          type: effectiveDiscipline,
          searchString: searchData,
          providerId: effectiveProviderId,
          page: pageNo,
          pageSize: 10,
        },
        otherPatientSourceAbort?.token
      )
    );
  };

  useEffect(() => {
    if (pageNo === 0) setOtherPatientList(otherPatientData?.content || []);
    else if (Array.isArray(otherPatientData?.content))
      setOtherPatientList((prev: any) => [...prev, ...otherPatientData?.content]);
  }, [otherPatientData?.content]);

  // const handleScrollCapture = (event: any) => {
  //   const scrollThreshold = 1;
  //   console.log(event);
  //   if (
  //     Math.abs(
  //       (event.target as any).scrollHeight -
  //         (event.target as any).scrollTop -
  //         (event.target as any).clientHeight
  //     ) <= scrollThreshold &&
  //     Math.ceil((event.target as any).scrollTop) !== 0
  //   ) {
  //     console.log('Aditya');
  //     // if (patientList?.length < selectedPatientList?.totalCount) {
  //     setPageNo((prev: any) => prev + 1);
  //     // }
  //   }
  // };

  useEffect(() => {
    if (pageNo === 0) setOtherPatientList(otherPatientData?.content || []);
    else if (Array.isArray(otherPatientData?.content))
      setOtherPatientList((prev: any) => [...prev, ...otherPatientData?.content]);
  }, [otherPatientData?.content]);

  const handleScrollCapture = (event: any) => {
    const scrollThreshold = 1;
    if (
      Math.abs(
        (event.target as any).scrollHeight -
          (event.target as any).scrollTop -
          (event.target as any).clientHeight
      ) <= scrollThreshold &&
      Math.ceil((event.target as any).scrollTop) !== 0
    ) {
      // if (patientList?.length < selectedPatientList?.totalCount) {
      setPageNo((prev: any) => prev + 1);
      // }
    }
  };

  useEffect(() => {
    if (providerData && selectedDate) callOtherSessionOther();
    return () => {
      otherPatientSourceAbort.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, searchData, pageNo, onBehalfTarget?.uuid]);
  useEffect(() => {
    if (soapNotesResponse)
      if (providerData && selectedDate && soapNotesResponse?.formId) callOtherSessionOther();
  }, [soapNotesResponse]);

  const callGetPatientList = () => {
    dispatch(
      getDailySessionPatientList(
        {
          providerId: effectiveProviderId,
          date: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          searchString: searchData,
        },
        patientSourceAbort?.token
      )
    );
  };

  // Refresh both patient lists (and show a SimpleSnackbar) once a DSN delete resolves.
  // Detect the loading true→false transition so a persisted/stale result doesn't re-trigger.
  const deleteDsnState = useSelector((state: any) => state?.deleteDailySessionNoteReducer);
  const wasDeletingDsnRef = useRef(false);
  const [deletePopUp, setDeletePopUp] = useState({ message: '', type: '' });
  useEffect(() => {
    if (deleteDsnState?.loading) {
      wasDeletingDsnRef.current = true;
      return;
    }
    if (!wasDeletingDsnRef.current) return;
    wasDeletingDsnRef.current = false;

    if (deleteDsnState?.isSuccess) {
      callGetPatientList();
      callOtherSessionOther();
      setDeletePopUp({
        message: deleteDsnState?.response?.message || 'Daily Session Note deleted successfully.',
        type: 'SUCCESS',
      });
    } else if (deleteDsnState?.response) {
      setDeletePopUp({
        message:
          deleteDsnState?.response?.message ||
          deleteDsnState?.response?.error ||
          'Failed to delete the Daily Session Note.',
        type: 'ERROR',
      });
    }
    dispatch(resetDeleteDailySessionNote());
    setTimeout(() => setDeletePopUp({ message: '', type: '' }), 3000);
  }, [deleteDsnState?.loading, deleteDsnState?.isSuccess]);

  // Confirm an ARCHIVE with the same snackbar the delete uses, and nothing else.
  //
  // Deliberately does NOT refetch the two patient lists the delete block above does: on this page
  // the note stays on screen after it is archived (BOPS-175 keeps it, struck through and badged,
  // via view-soap-notes' local `recentAction`), and re-pulling the lists here tears that state down
  // to sync a row the user is still looking at. The Episode of Care lists keep their existing
  // refetch — see completed-document-list / signature-pending-list, where the row really does have
  // to leave the list. The consequence here is that the day's freed-up slot (Start Note) only comes
  // back on the next natural list load. Keeps the loading true→false guard so a persisted or stale
  // result cannot re-fire the toast.
  const archiveDsnState = useSelector((state: any) => state?.archiveDailySessionNoteReducer);
  const wasArchivingDsnRef = useRef(false);
  useEffect(() => {
    if (archiveDsnState?.loading) {
      wasArchivingDsnRef.current = true;
      return;
    }
    if (!wasArchivingDsnRef.current) return;
    wasArchivingDsnRef.current = false;

    if (archiveDsnState?.isSuccess) {
      setDeletePopUp({
        message: archiveDsnState?.response?.message || 'Daily Session Note archived successfully.',
        type: 'SUCCESS',
      });
      dispatch(resetArchiveDailySessionNote());
      setTimeout(() => setDeletePopUp({ message: '', type: '' }), 3000);
    }
  }, [archiveDsnState?.loading, archiveDsnState?.isSuccess]);

  useEffect(() => {
    if (!!stateDashboard) {
      setSelectedPatient(stateDashboard);
      return;
    }
  }, [stateDashboard]);

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (!isDocLoader.loader) {
      if (selectedPatient?.name) callGetPatientList();
      else {
        callOtherSessionOther();
      }
    }
  }, [isDocLoader]);

  const checkSignDailyNotes = useSelector((state: any) => state.checkSignatureDailyNotesReducer);

  useEffect(() => {
    if (checkSignDailyNotes?.isCheckSign) {
      if (selectedPatient?.name) callGetPatientList();
      else callOtherSessionOther();
    }
  }, [checkSignDailyNotes]);

  const newDocumentSignReducer = useSelector((state: any) => state.newDocumentSignReducer);
  const undoDocumentSignResponse = useSelector((state: any) => state.undoDocumentSignReducer);

  useEffect(() => {
    if (newDocumentSignReducer?.response !== null && newDocumentSignReducer?.isSuccess) {
      if (selectedPatient?.name) callGetPatientList();
      else callOtherSessionOther();
      dispatch(resetNewDocumentSignAction());
    }
  }, [newDocumentSignReducer]);

  //  BRK-2086: the proxy sign (DSN signed on behalf from the signed-note pane) lands in its own
  //  reducer — refresh the lists the same way the direct sign above does, so the note flips to
  //  SIGNED without a manual reload.
  const addSignOnBehalfReducer = useSelector((state: any) => state.addSignOnBehalfReducer);
  useEffect(() => {
    if (addSignOnBehalfReducer?.response !== null && addSignOnBehalfReducer?.isSignOnBehalf) {
      if (selectedPatient?.name) callGetPatientList();
      else callOtherSessionOther();
      dispatch(resetSignOnBehalfAction());
    }
  }, [addSignOnBehalfReducer]);

  useEffect(() => {
    if (undoDocumentSignResponse?.isSuccess) {
      if (selectedPatient?.name) callGetPatientList();
      else callOtherSessionOther();
      dispatch(resetUndoDocumentSignAction());
    }
  }, [undoDocumentSignResponse]);

  const bgColorProp: any = {
    NOT_STARTED: '#6366F1',
    IN_PROGRESS: '#6366F1',
    SIGNED: '#15B79E',
    FINALIZED: '#15B79E',
  };

  return (
    <>
      {deletePopUp?.message && (
        <SimpleSnackbar
          message={deletePopUp?.message}
          isError={deletePopUp?.type === 'ERROR'}
        />
      )}
      <Stack
        sx={{ minWidth: isOpen ? '232px' : '', height: '100%' }}
        gap="1rem"
      >
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent={isOpen ? 'end' : 'center'}
        >
          {/* {isOpen && (
            <Typography variant="customHeading">
              {new Date(selectedDate)?.toDateString() === new Date()?.toDateString()
                ? `Today's Patients`
                : 'Patients'}
              <Chip
                sx={{ marginLeft: '.5rem' }}
                label={patientList?.length || 0}
              />
            </Typography>
          )} */}
          {/* 
          <IconButton onClick={() => setIsOpen((prev: any) => !prev)}>
            {isOpen ? <ChevronLeftOutlinedIcon /> : <ChevronRightOutlinedIcon />}
          </IconButton> */}
        </Stack>
        <Stack flexDirection={'row'}>
          <SearchBox
            setSearchData={setSearchData}
            searchData={searchData}
            setIsOpen={setIsOpen}
            isOpen={isOpen}
            placeholder={isOpen ? 'Search' : ''}
          />

          <IconButton
            sx={{ padding: '0px' }}
            data-testid={dsnPageTestId('patient-list-toggle')}
            onClick={() => setIsOpen((prev: any) => !prev)}
          >
            {isOpen ? <ChevronLeftOutlinedIcon /> : <ChevronRightOutlinedIcon />}
          </IconButton>
        </Stack>
        <Box sx={{ maxHeight: '65vh', overflow: 'auto ' }}>
          <Accordion disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography variant="customHeading">
                {new Date(selectedDate)?.toDateString() === new Date()?.toDateString()
                  ? !isOpen
                    ? 'TP'
                    : `Today's Patients`
                  : isOpen
                  ? 'Patients'
                  : 'P'}
                {isOpen && (
                  <Chip
                    sx={{ marginLeft: '.5rem' }}
                    label={patientList?.length || 0}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: isOpen ? '8px 16px 16px' : '0 2px 2px 2px' }}>
              <Stack gap="1rem">
                {!patientResponse?.loading &&
                  patientList?.map((patient: any, index: number) => {
                    const therapyNames = patient?.taskNames?.filter(
                      (therapy: any) => therapy !== null
                    );
                    return (
                      <Stack
                        flexDirection="row"
                        gap=".75rem"
                        key={index}
                        data-testid={dsnPageRowTestId('patient-row', patient?.uuid)}
                        onClick={(e) => setSelectedPatient(patient)}
                        alignItems="center"
                        sx={{
                          backgroundColor:
                            selectedPatient?.uuid === patient?.uuid ? 'rgb(206 207 255 / 50%)' : '',
                          borderRadius: selectedPatient?.uuid === patient?.uuid ? '12px' : '0px',

                          paddingTop: '.7rem',
                          paddingRight: {
                            md: '0rem',
                            lg: '.2rem',
                            xl: '.2rem',
                          },
                          paddingBottom: '.5rem',
                          paddingLeft: '.5rem',
                          cursor: 'pointer',
                        }}
                      >
                        <Avatar
                          sx={{ textTransform: 'uppercase !important' }}
                          src={`data:image/png;base64,${patient?.profileImage}`}
                        >{`${patient?.lastName?.at(0)} ${patient?.firstName?.at(0)}   `}</Avatar>
                        {isOpen && (
                          <Stack sx={{ width: '100%' }}>
                            <Stack
                              justifyContent="space-between"
                              flexDirection="row"
                              alignItems="center"
                            >
                              {patient?.name?.length > 20 && (
                                <Tooltip title={patient?.name || ''}>
                                  <Typography variant="subtitle2">
                                    {`${
                                      patient?.lastName + ', ' + patient?.firstName?.slice(0, 20)
                                    }...`}
                                  </Typography>
                                </Tooltip>
                              )}
                              {patient?.name?.length <= 20 && (
                                <Typography variant="subtitle2">
                                  {patient?.lastName + ', ' + patient?.firstName}
                                </Typography>
                              )}
                              <Stack
                                alignItems="center"
                                flexDirection="row"
                                gap=".3rem"
                                sx={{ minWidth: 'fit-content' }}
                              >
                                <Box
                                  sx={{
                                    width: '7px',
                                    height: '7px',
                                    backgroundColor: bgColorProp[patient?.status],
                                    borderRadius: '50%',
                                  }}
                                />
                                <Typography
                                  sx={{ fontSize: '10px', marginRight: '12px' }}
                                  variant="captionText"
                                >
                                  {patient?.status === 'NOT_STARTED' || !patient?.status
                                    ? 'Incomplete'
                                    : patient?.status
                                        ?.split('_')
                                        .map(
                                          (item: string) =>
                                            item.slice(0, 1) +
                                            item.toLowerCase().slice(1, item.length)
                                        )
                                        .join(' ')}
                                </Typography>
                              </Stack>
                            </Stack>
                            <Stack
                              flexDirection="row"
                              flexWrap="wrap"
                              gap=".5rem"
                              alignItems="center"
                            >
                              {patient?.therapyTypes?.map((type: any) =>
                                iconList?.filter((icon) => icon?.name === type)?.length > 0 ? (
                                  iconList?.filter((icon) => icon?.name === type)[0]?.icon
                                ) : (
                                  <></>
                                )
                              )}
                              {therapyNames?.length === 1 && (
                                <Box
                                  sx={{
                                    borderRadius: '12px',
                                    backgroundColor: '#ECFDFF',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    color: '#06AED4',
                                  }}
                                >
                                  {therapyNames[0]}
                                </Box>
                              )}
                              {therapyNames?.length !== 1 && therapyNames?.length > 1 && (
                                <LightTooltip
                                  title={therapyNames?.map((group: any) => (
                                    <Typography variant="captionText">{group}</Typography>
                                  ))}
                                >
                                  <Box
                                    sx={{
                                      borderRadius: '12px',
                                      backgroundColor: '#ECFDFF',
                                      padding: '4px 8px',
                                      fontSize: '11px',
                                      color: '#06AED4',
                                    }}
                                  >
                                    {therapyNames[0]}
                                    +1
                                  </Box>
                                </LightTooltip>
                              )}
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                    );
                  })}
                {patientResponse?.loading && (
                  <>
                    {Array.from(Array(3), () => (
                      <>
                        {' '}
                        <Skeleton
                          height={50}
                          variant="rounded"
                        />
                        <Skeleton
                          variant="rounded"
                          height={50}
                          animation="wave"
                        />
                        <Skeleton
                          variant="rounded"
                          height={50}
                          animation={false}
                        />
                      </>
                    ))}
                  </>
                )}
                {(patientList?.length === 0 || patientList === null) &&
                  !patientResponse?.loading &&
                  isOpen && (
                    <Typography
                      variant="h6"
                      sx={{ textAlign: 'center' }}
                    >
                      No patient record found
                    </Typography>
                  )}
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography variant="customHeading">
                {isOpen ? 'Other Patients' : 'OP'}{' '}
                {/* {isOpen && (
                  <Chip
                    sx={{ marginLeft: '.5rem' }}
                    label={otherPatientData?.length || 0}
                  />
                )} */}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: isOpen ? '8px 16px 16px' : '0 2px 2px 2px' }}>
              <Stack gap="1rem">
                {otherPatientList?.map((patient: any, index: number) => {
                  const patientName = `${patient?.lastName}, ${patient?.firstName}`;
                  return (
                    <Stack
                      // ref={otherPatientList?.length === index + 1 ? lastPostElementRef : null}
                      flexDirection="row"
                      gap=".75rem"
                      key={patient?.uuid}
                      data-testid={dsnPageRowTestId('other-patient-row', patient?.uuid)}
                      onClick={(e) => setSelectedPatient(patient)}
                      alignItems="center"
                      sx={{
                        backgroundColor:
                          selectedPatient?.uuid === patient?.uuid ? 'rgb(206 207 255 / 50%)' : '',
                        borderRadius: selectedPatient?.uuid === patient?.uuid ? '12px' : '0px',

                        paddingTop: '.7rem',
                        paddingRight: {
                          md: '0rem',
                          lg: '.2rem',
                          xl: '.2rem',
                        },
                        paddingBottom: '.5rem',
                        paddingLeft: '.5rem',
                        cursor: 'pointer',
                      }}
                    >
                      <Avatar
                        sx={{ textTransform: 'uppercase !important' }}
                        src={`data:image/png;base64,${patient?.profileImage}`}
                      >{`${patient?.lastName?.at(0)}${patient?.firstName?.at(0)}`}</Avatar>
                      {isOpen && (
                        <Stack sx={{ width: '100%' }}>
                          <Stack
                            justifyContent="space-between"
                            flexDirection="row"
                            alignItems="center"
                          >
                            {patientName?.length > 20 && (
                              <Tooltip title={patientName || ''}>
                                <Typography variant="subtitle2">
                                  {`${patientName?.slice(0, 20)}...`}
                                </Typography>
                              </Tooltip>
                            )}
                            {patientName?.length <= 20 && (
                              <Typography variant="subtitle2">{patientName}</Typography>
                            )}
                            <Stack
                              alignItems="center"
                              flexDirection="row"
                              gap=".3rem"
                              sx={{ minWidth: 'fit-content' }}
                            >
                              <Box
                                sx={{
                                  width: '7px',
                                  height: '7px',
                                  backgroundColor:
                                    bgColorProp[patient?.status ? patient?.status : 'NOT_STARTED'],
                                  borderRadius: '50%',
                                }}
                              />
                              <Typography
                                sx={{ fontSize: '10px', marginRight: '12px', lineHeight: '0px' }}
                                variant="captionText"
                              >
                                {patient?.status === 'NOT_STARTED' || !patient?.status
                                  ? 'Incomplete'
                                  : patient?.status
                                      ?.split('_')
                                      .map(
                                        (item: string) =>
                                          item.slice(0, 1) +
                                          item.toLowerCase().slice(1, item.length)
                                      )
                                      .join(' ')}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  );
                })}

                {isOtherPatientDataLoading && otherPatientList?.length > 0 && (
                  <Stack
                    sx={{ width: '30px', height: '40px', margin: '1rem auto' }}
                    alignItems="center"
                  >
                    {' '}
                    <CircularProgress />
                  </Stack>
                )}

                {isOtherPatientDataLoading && otherPatientList?.length === 0 && (
                  <>
                    {Array.from(Array(3), () => (
                      <>
                        {' '}
                        <Skeleton
                          height={50}
                          variant="rounded"
                        />
                        <Skeleton
                          variant="rounded"
                          height={50}
                          animation="wave"
                        />
                        <Skeleton
                          variant="rounded"
                          height={50}
                          animation={false}
                        />
                      </>
                    ))}
                  </>
                )}

                {(otherPatientList?.length === 0 || otherPatientList === null) &&
                  !isOtherPatientDataLoading &&
                  isOpen && (
                    <Typography
                      variant="h6"
                      sx={{ textAlign: 'center' }}
                    >
                      No patient record found
                    </Typography>
                  )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Stack>
    </>
  );
};

export default PatientListing;
